import { DomainError, type TournamentEventBody } from '@ctd/core';
import { createContext, useCallback, useContext, useSyncExternalStore } from 'react';
import { useNotify } from './components/notices';
import { errorText } from './lib/download';
import type { TournamentRepo } from './lib/repo';
import type { SessionSnapshot, TournamentSession } from './lib/session';

export const RepoContext = createContext<TournamentRepo | null>(null);

export function useRepo(): TournamentRepo {
  const repo = useContext(RepoContext);
  if (repo === null) throw new Error('RepoContext is missing');
  return repo;
}

export const SessionContext = createContext<TournamentSession | null>(null);

export function useSession(): TournamentSession {
  const s = useContext(SessionContext);
  if (s === null) throw new Error('SessionContext is missing');
  return s;
}

/** The open tournament's state and log; re-renders on every change. */
export function useSnapshot(): SessionSnapshot {
  const s = useSession();
  return useSyncExternalStore(s.subscribe, s.getSnapshot);
}

/**
 * Dispatch an event, reporting a rule violation (DomainError) or any other
 * failure as an error notice. Returns true when the event was applied.
 */
export function useDispatch(): (body: TournamentEventBody, success?: string) => boolean {
  const session = useSession();
  const notify = useNotify();
  return useCallback(
    (body, success) => {
      try {
        session.dispatch(body);
        if (success !== undefined) notify(success);
        return true;
      } catch (e) {
        notify(
          e instanceof DomainError ? errorText(e) : `Unexpected error: ${errorText(e)}`,
          'error',
        );
        return false;
      }
    },
    [session, notify],
  );
}
